import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Search Console connection — GSC_INTEGRATION.md stage 1.
 * Backend: `GET`/`PATCH /api/v1/gsc/connection`, `POST /api/v1/gsc/connection/sync`.
 *
 * Mirrors `GscConnectionView` in `backend/src/services/gsc-connection.ts`. `statusLine` and
 * `scopeLine` are the backend's copy, rendered as-is.
 */
export type GscConnectionStatus = 'pending' | 'ok' | 'no_access' | 'expired' | 'error';

export interface GscConnectionView {
  property: string;
  propertyType: 'domain' | 'url_prefix';
  domain: string | null;
  credential: 'service_account' | 'owner_oauth';
  credentialLabel: string;
  status: GscConnectionStatus;
  statusLine: string;
  lastSyncedAt: string | null;
  dataThrough: string | null;
  lastError: string | null;
  brandTerms: string[];
  scopeLine: string;
  serpQueryLine: string;
  pages: { gsc: number; brandOnly: number; newPage: number; lowData: number; unmatched: number } | null;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class GscConnectionService {
  private readonly API_URL = environment.apiUrl + '/v1/gsc/connection';

  constructor(private http: HttpClient) {}

  /** `data: null` when the workspace has not connected Search Console. */
  get(): Observable<Envelope<GscConnectionView | null>> {
    return this.http.get<Envelope<GscConnectionView | null>>(this.API_URL);
  }

  updateBrandTerms(brandTerms: string[]): Observable<Envelope<GscConnectionView>> {
    return this.http.patch<Envelope<GscConnectionView>>(this.API_URL, { brandTerms });
  }

  sync(): Observable<Envelope<{ started: boolean }>> {
    return this.http.post<Envelope<{ started: boolean }>>(`${this.API_URL}/sync`, {});
  }
}
